import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, WarningAmber } from '@mui/icons-material';
import { AccountDeactivate } from '../../../types/user';

interface DeactivateAccountModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (password: string) => void;
  isLoading: boolean;
  error?: string | null;
}

export const DeactivateAccountModal: React.FC<DeactivateAccountModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  error,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AccountDeactivate>();

  const [showPassword, setShowPassword] = useState(false);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: AccountDeactivate) => {
    onConfirm(data.password);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningAmber />
        계정 비활성화
      </DialogTitle>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
            정말 계정을 비활성화하시겠습니까? <br />
            이 작업은 되돌릴 수 없으며, 모든 데이터가 접근 불가능하게 됩니다.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            계속하려면 비밀번호를 입력하세요.
          </Typography>

          <Controller
            name="password"
            control={control}
            rules={{ required: '비밀번호를 입력해주세요.' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="비밀번호"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                autoFocus
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={isLoading} variant="outlined" color="inherit">
            취소
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ fontWeight: 600 }}
          >
            {isLoading ? '처리 중...' : '확인'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
